import React, { useState, useEffect, memo, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Avatar,
  Snackbar,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  PhotoCamera as PhotoCameraIcon,
  Business as BusinessIcon,
  School as SchoolIcon,
  AttachMoney as AttachMoneyIcon,
  LinkedIn as LinkedInIcon
} from '@mui/icons-material';
import { getAuthToken, getUserData } from '../../utils/authUtils';
import UpdateRoleButton from './UpdateRoleButton';
import ProfileSaveButton from './ProfileSaveButton';
import AvailabilityManagerHub from '../Professional/AvailabilityManagerHub';
import GoogleCalendarConnect from '../Professional/GoogleCalendarConnect';
import { API_URL } from '../../utils/apiConfig';

// Import shadcn/ui Tabs
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../UI/tabs';

// Use centralized API configuration
console.log('ProfessionalPublicProfileSetup API_URL from config:', API_URL);

// Use the full URL including the server address and port
const API_BASE = `${API_URL}/api/professionalprofiles`;
console.log('ProfessionalPublicProfileSetup component loaded with API_BASE:', API_BASE);

const defaultProfile = (userData) => ({
  name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
  firstName: userData.firstName || '',
  lastName: userData.lastName || '',
  preferredName: userData.preferredName || '',
  title: userData.title || userData.position || '',
  company: userData.company || '',
  position: userData.position || userData.title || '',
  headline: userData.headline || '',
  industry: userData.industry || 'Technology',
  seniority: userData.seniority || '',
  hourlyRate: userData.hourlyRate || 75,
  rateCurrency: userData.rateCurrency || 'GBP',
  bio: userData.bio || '',
  expertise: userData.expertise || '',
  profilePicture: userData.profilePicture || '',
  email: userData.email || '',
  linkedIn: userData.linkedIn || userData.linkedinUrl || '',
  meetingType: 'virtual' // All meetings are virtual by default
});

// Helper function to generate default headline
const generateDefaultHeadline = (position, company) => {
  if (position && company) {
    return `${position} at ${company}`;
  } else if (position) {
    return position;
  } else if (company) {
    return `Professional at ${company}`;
  }
  return '';
};

// Make this a memoized component
const ProfessionalPublicProfileSetup = memo(({ onSave }) => {
  // Add a ref to track if we've already fetched data
  const hasFetchedRef = useRef(false);
  const saveInProgressRef = useRef(false);

  const user = getUserData();
  const defaultValues = defaultProfile(user || {});

  // Initialize with defaults
  const [profile, setProfile] = useState(defaultValues);
  const [preview, setPreview] = useState(defaultValues);

  // Compute the effective headline (user input or auto-generated)
  const getEffectiveHeadline = useCallback((profileData) => {
    if (profileData?.headline) {
      return profileData.headline;
    }
    return generateDefaultHeadline(profileData?.position, profileData?.company);
  }, []);
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [fetchFailed, setFetchFailed] = useState(false);
  const [statistics, setStatistics] = useState({
    totalSessions: 0,
    completedSessions: 0,
    totalConnections: 0,
    memberSince: user?.createdAt || new Date().toISOString()
  });

  const isRoleProfessional = user?.role === 'professional';

  // Fetch statistics data
  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;

        // Fetch invitations - only received invitations for professionals (sessions booked with you)
        const invitationsResponse = await fetch(`${API_URL}/api/invitations?type=received`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Fetch threads to count unique connections
        const threadsResponse = await fetch(`${API_URL}/api/messages/threads`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (invitationsResponse.ok && threadsResponse.ok) {
          const invitationsData = await invitationsResponse.json();
          const threadsData = await threadsResponse.json();

          const invitations = invitationsData.data?.invitations || [];
          const threads = threadsData.data?.threads || [];

          console.log('🔍 Raw API Data:', {
            invitationsResponse: invitationsData,
            threadsResponse: threadsData,
            invitationsCount: invitations.length,
            threadsCount: threads.length
          });

          // Log all invitation statuses to see what we have
          const statusCounts = invitations.reduce((acc, inv) => {
            acc[inv.status] = (acc[inv.status] || 0) + 1;
            return acc;
          }, {});
          console.log('📊 Invitation status breakdown:', statusCounts);

          // Filter invitations by status
          // Completed sessions = only 'completed' status
          const completedInvitations = invitations.filter(inv => inv.status === 'completed');

          // Total sessions = all booked/confirmed sessions (excluding pending requests and cancelled)
          // This includes: accepted, confirmed, in_progress, and completed
          const allSessionInvitations = invitations.filter(inv =>
            ['accepted', 'confirmed', 'in_progress', 'completed'].includes(inv.status)
          );

          // Total connections = number of unique chat threads
          const uniqueConnections = threads.length;

          console.log('📊 Statistics calculated:', {
            totalInvitations: invitations.length,
            allSessions: allSessionInvitations.length,
            completed: completedInvitations.length,
            threads: threads.length,
            statusCounts
          });

          setStatistics({
            totalSessions: allSessionInvitations.length,
            completedSessions: completedInvitations.length,
            totalConnections: uniqueConnections,
            memberSince: user?.createdAt || new Date().toISOString()
          });
        }
      } catch (error) {
        console.error('Error fetching statistics:', error);
      }
    };

    if (isRoleProfessional) {
      fetchStatistics();
    }
  }, [isRoleProfessional, user]);

  // Listen for update events from other components
  useEffect(() => {
    const handleProfileUpdated = (event) => {
      console.log('🔔 Received profileUpdated event:', event.detail);
      if (event.detail?.profile) {
        console.log('Updating state with event data');
        setProfile(event.detail.profile);
        setPreview(event.detail.profile);
      }
    };

    // Add event listener
    window.addEventListener('professionalProfileUpdated', handleProfileUpdated);

    // Cleanup
    return () => {
      window.removeEventListener('professionalProfileUpdated', handleProfileUpdated);
    };
  }, []);

  // Add loading timeout effect
  useEffect(() => {
    if (loading) {
      // If still loading after 5 seconds, show extended UI
      const timeoutId = setTimeout(() => {
        setLoadingTimeout(true);
      }, 5000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [loading]);

  // Memoize the handlers to prevent unnecessary re-renders
  const handleChange = useCallback((field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    const updated = { ...profile, [field]: value };
    setProfile(updated);
    setPreview(updated);
  }, [profile]);


  const handleProfilePictureChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePictureFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePicturePreview(e.target.result);
        const updated = { ...profile, profilePicture: e.target.result };
        setProfile(updated);
        setPreview(updated);
      };
      reader.readAsDataURL(file);
    }
  }, [profile]);

  const handleCloseSnackbar = useCallback(() => 
    setSnackbar(prev => ({ ...prev, open: false })), []);

  // Fetch profile from backend on mount
  useEffect(() => {
    console.log('Component mounted - INITIAL RENDER');

    // CRITICAL: Only fetch once during the entire component lifecycle
    if (hasFetchedRef.current) {
      console.log('Skipping fetch - already fetched once in this session');
      setLoading(false); // Ensure loading is cleared even if we skip fetch
      return;
    }

    let isMounted = true;

    const fetchProfile = async () => {
      if (!isMounted) return;

      setLoading(true);
      console.log('⏳ Loading state set to TRUE');

      try {
        const token = getAuthToken();
        if (!token) {
          console.warn('No auth token available - using defaults');
          if (isMounted) {
            const defaultData = defaultProfile(user || {});
            setProfile(defaultData);
            setPreview(defaultData);
            setLoading(false);
          }
          console.log('⏳ No token: Loading state set to FALSE');
          return;
        }

        const requestUrl = `${API_BASE}/me`;
        console.log('🔄 Fetching profile from:', requestUrl);

        // Mark as fetched immediately to prevent double fetches
        hasFetchedRef.current = true;

        const res = await fetch(requestUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        console.log(`🔄 Profile API response status: ${res.status}`);

        if (res.status === 401) {
          console.warn('Unauthorized - auth token may be invalid');
          if (isMounted) {
            setFetchFailed(true);
            const defaultData = defaultProfile(user || {});
            setProfile(defaultData);
            setPreview(defaultData);
            setLoading(false);
            console.log('⏳ Auth error: Loading state set to FALSE');
          }
          return;
        }

        const data = await res.json();
        console.log('🔄 Profile API response data:', data);

        if (isMounted) {
          if (data.success && data.data && data.data.profile) {
            const serverProfile = data.data.profile;
            console.log('Success response, using server data:', serverProfile);

            // Map rate to hourlyRate for UI consistency (note: this is actually a 30-min session rate)
            if (serverProfile.rate !== undefined && serverProfile.rate > 0) {
              serverProfile.hourlyRate = serverProfile.rate;
              serverProfile.rateCurrency = serverProfile.rateCurrency || 'GBP';
              console.log(`💰 Loading rate: ${serverProfile.hourlyRate} ${serverProfile.rateCurrency}/30-min session`);
            } else {
              serverProfile.hourlyRate = serverProfile.hourlyRate || 75;
              serverProfile.rateCurrency = serverProfile.rateCurrency || 'GBP';
              console.log(`💰 Using default: ${serverProfile.hourlyRate} ${serverProfile.rateCurrency}/30-min session`);
            }

            // Ensure meetingType has a default value
            if (!serverProfile.meetingType) {
              serverProfile.meetingType = 'virtual';
            }

            // Ensure firstName and lastName are populated from user data
            if (!serverProfile.firstName) {
              serverProfile.firstName = user?.firstName || '';
            }
            if (!serverProfile.lastName) {
              serverProfile.lastName = user?.lastName || '';
            }

            // Map linkedinUrl to linkedIn for UI consistency
            if (serverProfile.linkedinUrl && !serverProfile.linkedIn) {
              serverProfile.linkedIn = serverProfile.linkedinUrl;
            }

            setProfile(serverProfile);
            setPreview(serverProfile);
          } else {
            console.log('Success response but no profile data, using defaults');
            const defaultData = defaultProfile(user || {});
            setProfile(defaultData);
            setPreview(defaultData);
          }
          setLoading(false);
          console.log('⏳ Fetch complete: Loading state set to FALSE');
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
        if (isMounted) {
          setFetchFailed(true);
          const defaultData = defaultProfile(user || {});
          setProfile(defaultData);
          setPreview(defaultData);
          setLoading(false);
          console.log('⏳ Fetch error: Loading state set to FALSE');
        }
      }
    };

    // Only fetch if professional role and haven't fetched before
    if (isRoleProfessional && !hasFetchedRef.current) {
      console.log('Starting profile fetch (first and only attempt)');
      fetchProfile().catch(err => {
        console.error('Unhandled fetch error:', err);
        if (isMounted) {
          const defaultData = defaultProfile(user || {});
          setProfile(defaultData);
          setPreview(defaultData);
          setLoading(false);
          console.log('⏳ Unhandled error: Loading state set to FALSE');
        }
      });
    } else {
      if (!isRoleProfessional) {
        console.log('Not a professional role, skipping fetch');
      } else {
        console.log('Already fetched once, not fetching again');
      }
      setLoading(false);
      console.log('⏳ Skip condition: Loading state set to FALSE');
    }

    return () => {
      console.log('Component unmounting - cleanup');
      isMounted = false;
    };
  }, []); // Empty dependency array - only run once

  // Handle save - memoized version
  const handleSave = useCallback(async () => {
    // Prevent multiple concurrent save attempts
    if (saveInProgressRef.current) {
      console.log('Save already in progress, ignoring this request');
      return;
    }
    
    saveInProgressRef.current = true;

    try {
      console.log('🚀 COMPREHENSIVE SAVE OPERATION STARTED');
      console.log('📋 Profile data to save:', profile);

      // Get auth token at the beginning of the save operation
      const token = getAuthToken();
      if (!token) {
        setSnackbar({
          open: true,
          message: 'Authentication required. Please log in again.',
          severity: 'error'
        });
        saveInProgressRef.current = false;
        return;
      }

      // Handle profile picture upload if present
      if (profilePictureFile) {
        console.log('📸 Uploading profile picture...');
        try {
          const formData = new FormData();
          formData.append('profilePicture', profilePictureFile);

          const uploadUrl = `${API_URL}/api/files/upload/profile-photo`;
          console.log('📸 Uploading to:', uploadUrl);

          const uploadResponse = await fetch(uploadUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });

          if (uploadResponse.ok) {
            const uploadData = await uploadResponse.json();
            if (uploadData.success) {
              // Update profile with uploaded picture URL
              profile.profilePicture = uploadData.data.url;
              console.log('✅ Profile picture uploaded successfully:', uploadData.data.url);
            }
          }
        } catch (uploadError) {
          console.error('❌ Profile picture upload error:', uploadError);
          setSnackbar({
            open: true,
            message: 'Failed to upload profile picture',
            severity: 'warning'
          });
        }
      }

      // Clean, simple: ONE API call to update everything
      const requestUrl = `${API_URL}/api/professionalprofiles`;
      console.log('💾 Saving profile to:', requestUrl);

      // Prepare data to send
      const fieldsToSend = {
        ...profile,
        // Ensure firstName/lastName are synced with name
        firstName: profile.firstName || (profile.name ? profile.name.split(' ')[0] : ''),
        lastName: profile.lastName || (profile.name ? profile.name.split(' ').slice(1).join(' ') : ''),
        name: profile.name || `${profile.firstName || ''} ${profile.lastName || ''}`.trim(),
        // Store hourlyRate as rate (30-min session rate)
        rate: profile.hourlyRate || 75,
        rateCurrency: profile.rateCurrency || 'GBP',
        // Map linkedIn to linkedinUrl for backend storage
        linkedinUrl: profile.linkedIn || '',
        // Ensure arrays
        industries: Array.isArray(profile.industries) ? profile.industries : [],
        skills: Array.isArray(profile.skills) ? profile.skills : []
      };

      console.log('📤 Data being sent:', JSON.stringify(fieldsToSend, null, 2));

      try {
        const response = await fetch(requestUrl, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(fieldsToSend),
        });

        console.log(`💾 API Response status: ${response.status}`);

        if (response.ok) {
          const data = await response.json();
          console.log('💾 API Response:', data);

          if (data.success) {
            console.log('✅ Profile saved successfully');

            // Update localStorage with the updated user data
            if (data.data.user) {
              localStorage.setItem('userData', JSON.stringify(data.data.user));

              // Dispatch event for other components
              window.dispatchEvent(new CustomEvent('userDataUpdated', {
                detail: data.data.user
              }));
            }

            setSnackbar({
              open: true,
              message: 'Profile updated successfully!',
              severity: 'success'
            });
          } else {
            console.error('❌ Save failed:', data);
            setSnackbar({
              open: true,
              message: data.error?.message || 'Failed to save profile',
              severity: 'error'
            });
          }
        } else {
          const errorText = await response.text();
          console.error('❌ API error:', response.status, errorText);
          setSnackbar({
            open: true,
            message: `Server error (${response.status})`,
            severity: 'error'
          });
        }
      } catch (fetchError) {
        console.error('❌ Network error:', fetchError);
        setSnackbar({
          open: true,
          message: `Network error: ${fetchError.message}`,
          severity: 'error'
        });
      }
    } catch (err) {
      console.error('💥 SAVE OPERATION FAILED:', err);
      setSnackbar({ 
        open: true, 
        message: `Unexpected error saving profile: ${err.message}`, 
        severity: 'error' 
      });
    } finally {
      saveInProgressRef.current = false;
      console.log('🏁 SAVE OPERATION ENDED');
    }
  }, [profile]);

  // Update the loading render
  if (loading) {
    return (
      <Paper sx={{ p: 3, my: 2 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Loading Your Profile...
          </Typography>
          {loadingTimeout && (
            <>
              <Typography variant="body2" color="text.secondary" paragraph>
                This is taking longer than expected.
              </Typography>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => {
                  // Force loading to end and use default profile
                  setLoading(false);
                  const defaultData = defaultProfile(user || {});
                  setProfile(defaultData);
                  setPreview(defaultData);
                }}
                sx={{ mt: 1 }}
              >
                Skip Loading
              </Button>
            </>
          )}
        </Box>
      </Paper>
    );
  }
  
  if (!isRoleProfessional) {
    return (
      <Paper sx={{ p: 3, my: 2 }}>
        <Typography variant="h5" gutterBottom>
          Professional Role Required
        </Typography>
        <Typography paragraph>
          Your account is currently set as a job seeker. To create a professional profile,
          you need to update your role to "professional".
        </Typography>
        <Box sx={{ mt: 2 }}>
          <UpdateRoleButton />
        </Box>
      </Paper>
    );
  }

  return (
    <div style={{ padding: '32px 16px' }}>
      <div className="mx-auto" style={{ maxWidth: '1280px' }}>
        {/* Page Header */}
        <h1 className="text-3xl font-normal mb-4 text-gray-900 dark:text-white">
          Professional Profile Setup
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-8">
          Manage your professional profile and availability to attract potential clients
        </p>

        {/* Tabs for Profile Info and Availability */}
      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="profile">Profile Information</TabsTrigger>
          <TabsTrigger value="availability">Availability</TabsTrigger>
        </TabsList>

        {/* Tab 1: Profile Information */}
        <TabsContent value="profile">
          {/* Grid Layout: Left column for photo, Right column for form */}
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Profile Picture */}
            <div className="space-y-6">
              <Card sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Box sx={{ position: 'relative', mb: 2 }}>
                      <Avatar
                        src={profilePicturePreview || profile?.profilePicture}
                        sx={{ width: 128, height: 128 }}
                      />
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="profile-picture-upload"
                        type="file"
                        onChange={handleProfilePictureChange}
                      />
                      <label htmlFor="profile-picture-upload">
                        <IconButton
                          color="primary"
                          component="span"
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            bgcolor: 'background.paper',
                            boxShadow: 1,
                            '&:hover': {
                              bgcolor: 'background.paper',
                            }
                          }}
                        >
                          <PhotoCameraIcon />
                        </IconButton>
                      </label>
                    </Box>
                    <Typography variant="h5" sx={{ mt: 2, fontWeight: 500 }}>
                      {profile?.firstName && profile?.lastName
                        ? `${profile.firstName} ${profile.lastName}`
                        : 'Your Name'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {getEffectiveHeadline(profile) || 'Professional'}
                    </Typography>
                    <Chip
                      label={`Member since ${new Date(statistics.memberSince).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        mt: 1.5,
                        bgcolor: 'action.hover',
                        borderColor: 'divider'
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>

              {/* Profile Statistics Card */}
              <Card sx={{ borderRadius: 3 }}>
                <CardHeader
                  title={
                    <Typography variant="h6" sx={{ fontSize: '1.125rem' }}>
                      Profile Statistics
                    </Typography>
                  }
                />
                <CardContent sx={{ pt: 0 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Total Sessions
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {statistics.totalSessions}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Completed
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {statistics.completedSessions}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Total Connections
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {statistics.totalConnections}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Profile Form */}
            <div className="lg:col-span-2">
              <Card sx={{ mb: 4, borderRadius: 3 }}>
                <CardHeader
                  avatar={<PersonIcon color="primary" />}
                  title="Profile Description"
                  subheader="Update your professional information and bio"
                  action={<EditIcon color="action" />}
                />
                <CardContent>
                  <Grid container spacing={3}>
                    {/* Name Fields */}
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="First Name"
                        value={profile?.firstName || ''}
                        onChange={handleChange('firstName')}
                        fullWidth
                        variant="outlined"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Last Name"
                        value={profile?.lastName || ''}
                        onChange={handleChange('lastName')}
                        fullWidth
                        variant="outlined"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Preferred Name"
                        value={profile?.preferredName || ''}
                        onChange={handleChange('preferredName')}
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                    {/* Professional Information */}
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Current Position"
                        value={profile?.position || ''}
                        onChange={handleChange('position')}
                        fullWidth
                        variant="outlined"
                        placeholder="e.g., Senior Software Engineer"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Company"
                        value={profile?.company || ''}
                        onChange={handleChange('company')}
                        fullWidth
                        variant="outlined"
                        placeholder="e.g., Google, Microsoft"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Headline"
                        value={profile?.headline || generateDefaultHeadline(profile?.position, profile?.company)}
                        onChange={handleChange('headline')}
                        fullWidth
                        variant="outlined"
                        placeholder="e.g., Helping startups scale globally"
                        InputProps={{
                          sx: {
                            '& input': {
                              color: profile?.headline ? 'inherit' : 'text.secondary'
                            }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Industry</InputLabel>
                        <Select
                          value={profile?.industry || 'Technology'}
                          onChange={handleChange('industry')}
                          label="Industry"
                        >
                          <MenuItem value="Technology">Technology</MenuItem>
                          <MenuItem value="Finance">Finance</MenuItem>
                          <MenuItem value="Healthcare">Healthcare</MenuItem>
                          <MenuItem value="Marketing">Marketing</MenuItem>
                          <MenuItem value="Consulting">Consulting</MenuItem>
                          <MenuItem value="Education">Education</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Years of Experience"
                        value={profile?.seniority || ''}
                        onChange={handleChange('seniority')}
                        fullWidth
                        variant="outlined"
                        placeholder="e.g., 5-10 years"
                        InputLabelProps={{
                          shrink: true
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel id="currency-label">Currency</InputLabel>
                        <Select
                          labelId="currency-label"
                          id="rateCurrency"
                          value={profile?.rateCurrency || 'GBP'}
                          label="Currency"
                          onChange={handleChange('rateCurrency')}
                        >
                          <MenuItem value="GBP">£ GBP</MenuItem>
                          <MenuItem value="USD">$ USD</MenuItem>
                          <MenuItem value="EUR">€ EUR</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        label="30-Min Rate"
                        type="number"
                        value={profile?.hourlyRate || 75}
                        onChange={handleChange('hourlyRate')}
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {profile?.rateCurrency === 'USD' ? '$' : profile?.rateCurrency === 'EUR' ? '€' : '£'}
                            </InputAdornment>
                          ),
                          inputProps: { min: 0, max: 1000 }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Professional Bio"
                        value={profile?.bio || ''}
                        onChange={handleChange('bio')}
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={4}
                        placeholder="Write a compelling bio that highlights your experience, achievements, and what makes you unique..."
                        helperText="Describe your professional background, key achievements, and what you can offer to clients"
                        InputLabelProps={{
                          shrink: true
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Areas of Expertise"
                        value={profile?.expertise || ''}
                        onChange={handleChange('expertise')}
                        fullWidth
                        variant="outlined"
                        placeholder="e.g., React, Product Management, Digital Marketing, Data Analysis"
                        InputLabelProps={{
                          shrink: true
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="LinkedIn Profile"
                        value={profile?.linkedIn || ''}
                        onChange={handleChange('linkedIn')}
                        fullWidth
                        variant="outlined"
                        placeholder="https://linkedin.com/in/yourprofile"
                        InputLabelProps={{
                          shrink: true
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <ProfileSaveButton
                          onClick={handleSave}
                          label="Save Profile Changes"
                          variant="contained"
                          size="large"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Tab 2: Availability */}
        <TabsContent value="availability">
          {/* Availability Management Section */}
          <div className="mb-6">
            <h2 className="text-2xl mb-4 text-gray-900 dark:text-white">Availability Management</h2>
            <AvailabilityManagerHub />
          </div>

          {/* Google Calendar Integration Section */}
          <div>
            <h2 className="text-2xl mb-4 text-gray-900 dark:text-white">Google Calendar Integration</h2>
            <GoogleCalendarConnect />
          </div>
        </TabsContent>
      </Tabs>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      </div>
    </div>
  );
});

export default ProfessionalPublicProfileSetup; 